/*
 * One-click unsubscribe. No login, no account lookup by the member, nothing to fill in.
 * The token in the link is the whole proof, and clicking twice is harmless.
 */
import { useEffect, useState } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  en: {
    working: 'Unsubscribing…',
    doneTitle: "You're unsubscribed.",
    doneBody: 'You will no longer receive Arvexis news, product updates or offers. Account, security, billing and service messages are separate and will continue.',
    alreadyTitle: 'You are already unsubscribed.',
    alreadyBody: 'Nothing changed. You are not receiving Arvexis marketing emails.',
    invalidTitle: 'This unsubscribe link is no longer valid.',
    invalidBody: 'You can turn marketing emails off at any time in Settings → Privacy.',
    home: 'Return to Arvexis',
  },
  es: {
    working: 'Cancelando la suscripción…',
    doneTitle: 'Has cancelado la suscripción.',
    doneBody: 'Ya no recibirás noticias, novedades del producto ni ofertas de Arvexis. Los mensajes de cuenta, seguridad, facturación y servicio son aparte y seguirán llegando.',
    alreadyTitle: 'Ya habías cancelado la suscripción.',
    alreadyBody: 'No se cambió nada. No estás recibiendo correos de marketing de Arvexis.',
    invalidTitle: 'Este enlace para cancelar la suscripción ya no es válido.',
    invalidBody: 'Puedes desactivar los correos de marketing en cualquier momento en Ajustes → Privacidad.',
    home: 'Volver a Arvexis',
  },
};

export default function UnsubscribePage() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';
  const urlLang = params.get('lang');
  const lang = (urlLang === 'es' || urlLang === 'en')
    ? urlLang
    : (localStorage.getItem('arvexis_lang') === 'es' ? 'es' : 'en');
  const c = COPY[lang];

  const [status, setStatus] = useState('working');   // working | done | already | invalid

  useEffect(() => {
    let alive = true;
    if (!token) { setStatus('invalid'); return undefined; }
    fetch(`${API}/marketing/unsubscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(async (r) => {
        if (!alive) return;
        if (!r.ok) { setStatus('invalid'); return; }
        const data = await r.json();
        setStatus(data?.already_unsubscribed ? 'already' : 'done');
      })
      .catch(() => { if (alive) setStatus('invalid'); });
    return () => { alive = false; };
  }, [token]);

  const title = status === 'done' ? c.doneTitle
    : status === 'already' ? c.alreadyTitle
      : status === 'invalid' ? c.invalidTitle : c.working;
  const body = status === 'done' ? c.doneBody
    : status === 'already' ? c.alreadyBody
      : status === 'invalid' ? c.invalidBody : '';

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: '#0F172A' }}
      data-testid="unsubscribe-surface"
    >
      <div style={{ maxWidth: 440, textAlign: 'left' }}>
        <h1
          className="text-2xl"
          style={{ color: '#F4EFE7', fontFamily: "'Cormorant Garamond', Georgia, serif", marginBottom: 14 }}
          data-testid="unsubscribe-title"
        >
          {title}
        </h1>
        {body && (
          <p
            className="text-sm"
            style={{ color: 'rgba(244,239,231,0.62)', lineHeight: 1.65 }}
            data-testid="unsubscribe-body"
          >
            {body}
          </p>
        )}
        <a
          href="/"
          className="text-sm"
          style={{ display: 'inline-block', marginTop: 26, color: '#E8C58F' }}
          data-testid="unsubscribe-home"
        >
          {c.home}
        </a>
      </div>
    </div>
  );
}
